// The flat row model shared by Combobox and MultiSelect: options in caller
// order, with a non-selectable header row before each run of options that
// share a `group`. Every row has the same fixed height, so a long list can
// be windowed by row index (ui/virtual/useWindow).
export interface ListboxOption {
  value: string
  label: string
  /** Text matched by the default filter. Defaults to `label`. */
  textValue?: string
  /** Rows sharing a group render under one labelled header. */
  group?: string
  /** 1 indents the row (a child under an emphasised base row). */
  depth?: 0 | 1
  /** Bold row, e.g. a selectable group-base row. */
  isEmphasized?: boolean
  disabled?: boolean
}

export interface ListboxOptionState {
  isActive: boolean
  isSelected: boolean
}

export type ListboxFilter = (option: ListboxOption, query: string) => boolean

export type ListboxRow =
  | { kind: 'header'; label: string; groupKey: number }
  | { kind: 'option'; option: ListboxOption; groupKey: number }

export interface ListboxModel {
  rows: ListboxRow[]
  /** Option value → row index. */
  indexOf: Map<string, number>
}

/** Case-insensitive substring match on `textValue ?? label`. */
export const defaultFilter: ListboxFilter = (option, query) =>
  (option.textValue ?? option.label).toLowerCase().includes(query.toLowerCase())

export function buildModel(
  options: readonly ListboxOption[],
  query: string,
  filter: ListboxFilter
): ListboxModel {
  const rows: ListboxRow[] = []
  const indexOf = new Map<string, number>()
  const q = query.trim()
  let currentGroup: string | undefined
  let groupKey = -1
  for (const option of options) {
    if (q && !filter(option, q)) continue
    if (option.group !== currentGroup) {
      currentGroup = option.group
      if (option.group === undefined) groupKey = -1
      else {
        groupKey = rows.length
        rows.push({ kind: 'header', label: option.group, groupKey })
      }
    }
    indexOf.set(option.value, rows.length)
    rows.push({ kind: 'option', option, groupKey })
  }
  return { rows, indexOf }
}

export function isSelectable(
  row: ListboxRow | undefined
): row is Extract<ListboxRow, { kind: 'option' }> {
  return row?.kind === 'option' && !row.option.disabled
}

/** The next selectable row from `from` in direction `step`, or `from` when there is none. */
export function stepSelectable(rows: readonly ListboxRow[], from: number, step: number): number {
  const dir = step < 0 ? -1 : 1
  let target = from
  let remaining = Math.abs(step)
  for (let i = from + dir; i >= 0 && i < rows.length && remaining > 0; i += dir) {
    if (isSelectable(rows[i])) {
      target = i
      remaining -= 1
    }
  }
  return target
}

export function firstSelectable(rows: readonly ListboxRow[]): number {
  return stepSelectable(rows, -1, 1)
}

export function lastSelectable(rows: readonly ListboxRow[]): number {
  const i = stepSelectable(rows, rows.length, -1)
  return i === rows.length ? -1 : i
}
